// @ts-nocheck
/**
 * ROI Visualization Component
 * Carbon Charts implementation for ROI data visualization
 * Epic 18: Enhanced marketing SEO with Carbon meta components
 */

import React, { useMemo } from 'react';
import { ComboChart, AreaChart, PieChart, BarChart } from '@carbon/charts-react';
import {
  Tile,
  Loading,
  InlineNotification,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Grid,
  Column,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableContainer,
} from '@carbon/react';
import { Analytics, ChartLineSmooth, ChartPie, ChartBar, TableOfContents } from '@carbon/icons-react';
import '@carbon/charts-react/styles.css';

interface ROIResults {
  annualSavings: number;
  threeYearROI: number;
  paybackPeriod: number;
  productivityGains: number;
  studentOutcomeImprovement: number;
  costPerStudent: number;
  breakdownByCategory: {
    [key: string]: number;
  };
}

interface ROIVisualizationProps {
  data: ROIResults;
  calculatorType:
    | 'student-performance'
    | 'teacher-productivity'
    | 'cost-savings'
    | 'learning-outcomes'
    | 'technology-investment';
  height?: number;
  loading?: boolean;
  error?: string;
}

const ROIVisualization: React.FC<ROIVisualizationProps> = ({
  data,
  calculatorType,
  height = 400,
  loading = false,
  error = '',
}) => {
  // Generate 3-year projection data
  const projectionData = useMemo(() => {
    if (!data) return [];

    const years = ['Year 1', 'Year 2', 'Year 3'];
    const baselineInvestment = 100000; // Assumed baseline

    return years.map((year, index) => {
      const multiplier = Math.pow(1 + data.threeYearROI / 100 / 3, index + 1);
      const cumulativeSavings = data.annualSavings * (index + 1);
      const roi = ((cumulativeSavings - baselineInvestment) / baselineInvestment) * 100;

      return {
        year,
        'Cumulative Savings': cumulativeSavings,
        Investment: baselineInvestment,
        'ROI %': Math.max(roi, -100), // Cap negative ROI for visualization
      };
    });
  }, [data]);

  // Generate category breakdown data for pie chart
  const categoryData = useMemo(() => {
    if (!data?.breakdownByCategory) return [];

    return Object.entries(data.breakdownByCategory)
      .filter(([_, value]) => value > 0)
      .map(([category, value]) => ({
        group: category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        value: value,
      }));
  }, [data?.breakdownByCategory]);

  // Monthly payback progression
  const paybackData = useMemo(() => {
    if (!data) return [];

    const months = Array.from({ length: Math.min(data.paybackPeriod + 6, 36) }, (_, i) => i + 1);
    const monthlySavings = data.annualSavings / 12;
    const initialInvestment = monthlySavings * data.paybackPeriod;

    return months.map(month => {
      const cumulativeSavings = monthlySavings * month;
      const netValue = cumulativeSavings - initialInvestment;

      return {
        month: `Month ${month}`,
        'Cumulative Savings': cumulativeSavings,
        'Net Value': netValue,
        'Break-even': 0,
      };
    });
  }, [data]);

  // Chart options with Sigma08 theme
  const getChartOptions = (chartType: string) => {
    const baseOptions = {
      height,
      theme: 'white',
      animations: {
        enabled: true,
      },
      color: {
        scale: {
          'Cumulative Savings': '#8a3ffc', // Galaxy Purple
          Investment: '#ffc700', // Saffron Orange
          'Net Value': '#0043ce', // Carbon Blue
          'ROI %': '#24a148', // Carbon Green
          'Break-even': '#525252', // Carbon Gray
          'Teacher Productivity': '#8a3ffc',
          'Student Outcomes': '#ffc700',
          'Cost Savings': '#0043ce',
          'Technology ROI': '#24a148',
          'Operational Efficiency': '#e60054', // Hillier Pink
        },
      },
      grid: {
        x: {
          enabled: false,
        },
        y: {
          enabled: true,
        },
      },
      legend: {
        enabled: true,
        position: 'top' as const,
        clickable: true,
      },
    };

    switch (chartType) {
      case 'projection':
        return {
          ...baseOptions,
          title: '3-Year ROI Projection',
          axes: {
            left: {
              title: 'Amount ($)',
              mapsTo: 'value',
            },
            right: {
              title: 'ROI (%)',
              mapsTo: 'roi',
              correspondingDatasets: ['ROI %'],
            },
            bottom: {
              title: 'Year',
              mapsTo: 'group',
              scaleType: 'labels' as const,
            },
          },
          comboChartTypes: [
            {
              type: 'simple-bar',
              options: {
                keys: ['Cumulative Savings', 'Investment'],
              },
            },
            {
              type: 'line',
              options: {
                keys: ['ROI %'],
              },
            },
          ],
        };

      case 'payback':
        return {
          ...baseOptions,
          title: 'Payback Period Analysis',
          axes: {
            left: {
              title: 'Amount ($)',
              mapsTo: 'value',
            },
            bottom: {
              title: 'Timeline',
              mapsTo: 'group',
              scaleType: 'labels' as const,
            },
          },
          curve: 'curveMonotoneX' as const,
          points: {
            enabled: true,
            radius: 3,
          },
        };

      case 'breakdown':
        return {
          ...baseOptions,
          title: 'ROI Breakdown by Category',
          pie: {
            alignment: 'center' as const,
            valueMapsTo: 'value',
          },
          donut: {
            center: {
              label: 'Total ROI',
            },
          },
          tooltip: {
            enabled: true,
            customHTML: (datapoint: any) => {
              if (!datapoint) return '';
              const percentage = ((datapoint.value / data.annualSavings) * 100).toFixed(1);
              return `
                <div class="bx--cc--tooltip">
                  <p class="bx--cc--tooltip-total">${datapoint.group}</p>
                  <ul class="bx--cc--tooltip-data">
                    <li>
                      <span class="bx--cc--tooltip-data__label">Value:</span>
                      <span class="bx--cc--tooltip-data__value">$${datapoint.value.toLocaleString()}</span>
                    </li>
                    <li>
                      <span class="bx--cc--tooltip-data__label">Percentage:</span>
                      <span class="bx--cc--tooltip-data__value">${percentage}%</span>
                    </li>
                  </ul>
                </div>
              `;
            },
          },
        };

      default:
        return baseOptions;
    }
  };

  // Transform data for different chart types
  const getChartData = (chartType: string) => {
    switch (chartType) {
      case 'projection':
        return projectionData.flatMap(item => [
          { group: item.year, key: 'Cumulative Savings', value: item['Cumulative Savings'] },
          { group: item.year, key: 'Investment', value: item['Investment'] },
          { group: item.year, key: 'ROI %', value: item['ROI %'], roi: item['ROI %'] },
        ]);

      case 'payback':
        return paybackData.flatMap(item => [
          { group: item.month, key: 'Cumulative Savings', value: item['Cumulative Savings'] },
          { group: item.month, key: 'Net Value', value: item['Net Value'] },
          { group: item.month, key: 'Break-even', value: item['Break-even'] },
        ]);

      case 'breakdown':
        return categoryData;

      default:
        return [];
    }
  };

  if (loading) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            gap: 'var(--cds-spacing-03)',
          }}
        >
          <Loading withOverlay={false} />
          <span style={{ color: 'var(--cds-text-secondary)' }}>Loading visualization data...</span>
        </div>
      </Tile>
    );
  }

  if (error) {
    return (
      <Tile>
        <InlineNotification title="Visualization Error" subtitle={error} kind="error" lowContrast hideCloseButton />
      </Tile>
    );
  }

  if (!data) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            color: 'var(--cds-text-secondary)',
          }}
        >
          <Analytics size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
          <span>No data available for visualization</span>
        </div>
      </Tile>
    );
  }

  return (
    <Tile>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--cds-spacing-03)',
          marginBottom: 'var(--cds-spacing-05)',
        }}
      >
        <Analytics size={20} style={{ color: 'var(--cds-interactive-01)' }} />
        <h4
          style={{
            fontSize: 'var(--cds-productive-heading-03-font-size)',
            fontWeight: 'var(--cds-productive-heading-03-font-weight)',
            lineHeight: 'var(--cds-productive-heading-03-line-height)',
            margin: 0,
          }}
        >
          ROI Analysis & Projections
        </h4>
      </div>

      <Tabs>
        <TabList>
          <Tab renderIcon={ChartLineSmooth}>3-Year Projection</Tab>
          <Tab renderIcon={ChartBar}>Payback Analysis</Tab>
          <Tab renderIcon={ChartPie}>Category Breakdown</Tab>
          <Tab renderIcon={TableOfContents}>Detailed Breakdown</Tab>
        </TabList>

        <TabPanels>
          {/* 3-Year Projection */}
          <TabPanel>
            <div
              style={{
                backgroundColor: 'var(--cds-layer-01)',
                borderRadius: 'var(--cds-border-radius)',
                padding: 'var(--cds-spacing-05)',
                marginTop: 'var(--cds-spacing-05)',
              }}
            >
              <ComboChart data={getChartData('projection')} options={getChartOptions('projection')} />

              <div
                style={{
                  marginTop: 'var(--cds-spacing-05)',
                  padding: 'var(--cds-spacing-05)',
                  backgroundColor: 'var(--cds-layer-02)',
                  borderRadius: 'var(--cds-border-radius)',
                }}
              >
                <Grid condensed>
                  <Column lg={4} md={2} sm={2}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          color: '#8a3ffc',
                        }}
                      >
                        {data.threeYearROI}%
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--cds-body-compact-01-font-size)',
                          color: 'var(--cds-text-secondary)',
                        }}
                      >
                        Total 3-Year ROI
                      </div>
                    </div>
                  </Column>
                  <Column lg={4} md={2} sm={2}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          color: '#ffc700',
                        }}
                      >
                        ${(data.annualSavings * 3).toLocaleString()}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--cds-body-compact-01-font-size)',
                          color: 'var(--cds-text-secondary)',
                        }}
                      >
                        Total 3-Year Savings
                      </div>
                    </div>
                  </Column>
                  <Column lg={4} md={2} sm={2}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          color: '#0043ce',
                        }}
                      >
                        {data.productivityGains}%
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--cds-body-compact-01-font-size)',
                          color: 'var(--cds-text-secondary)',
                        }}
                      >
                        Productivity Gains
                      </div>
                    </div>
                  </Column>
                  <Column lg={4} md={2} sm={2}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          color: '#24a148',
                        }}
                      >
                        {data.studentOutcomeImprovement}%
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--cds-body-compact-01-font-size)',
                          color: 'var(--cds-text-secondary)',
                        }}
                      >
                        Outcome Improvement
                      </div>
                    </div>
                  </Column>
                </Grid>
              </div>
            </div>
          </TabPanel>

          {/* Payback Analysis */}
          <TabPanel>
            <div
              style={{
                backgroundColor: 'var(--cds-layer-01)',
                borderRadius: 'var(--cds-border-radius)',
                padding: 'var(--cds-spacing-05)',
                marginTop: 'var(--cds-spacing-05)',
              }}
            >
              <AreaChart data={getChartData('payback')} options={getChartOptions('payback')} />

              <div
                style={{
                  marginTop: 'var(--cds-spacing-05)',
                  textAlign: 'center',
                  padding: 'var(--cds-spacing-05)',
                  backgroundColor: 'var(--cds-layer-02)',
                  borderRadius: 'var(--cds-border-radius)',
                }}
              >
                <p
                  style={{
                    fontSize: 'var(--cds-body-long-02-font-size)',
                    color: 'var(--cds-text-secondary)',
                    margin: 0,
                  }}
                >
                  Break-even point reached in <strong>{data.paybackPeriod} months</strong> with monthly savings of{' '}
                  <strong>${(data.annualSavings / 12).toLocaleString()}</strong>
                </p>
              </div>
            </div>
          </TabPanel>

          {/* Category Breakdown */}
          <TabPanel>
            <div
              style={{
                backgroundColor: 'var(--cds-layer-01)',
                borderRadius: 'var(--cds-border-radius)',
                padding: 'var(--cds-spacing-05)',
                marginTop: 'var(--cds-spacing-05)',
              }}
            >
              <PieChart data={getChartData('breakdown')} options={getChartOptions('breakdown')} />

              <div
                style={{
                  marginTop: 'var(--cds-spacing-05)',
                  padding: 'var(--cds-spacing-05)',
                  backgroundColor: 'var(--cds-layer-02)',
                  borderRadius: 'var(--cds-border-radius)',
                }}
              >
                <Grid condensed>
                  {categoryData.map((category, index) => (
                    <Column key={index} lg={8} md={4} sm={2}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: 'var(--cds-spacing-03) 0',
                          borderBottom: index < categoryData.length - 1 ? '1px solid var(--cds-border-subtle)' : 'none',
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>{category.group}</span>
                        <span
                          style={{
                            fontWeight: 600,
                            color: 'var(--cds-text-primary)',
                          }}
                        >
                          ${category.value.toLocaleString()}
                        </span>
                      </div>
                    </Column>
                  ))}
                </Grid>
              </div>
            </div>
          </TabPanel>

          {/* Detailed Breakdown DataTable */}
          <TabPanel>
            <div
              style={{
                backgroundColor: 'var(--cds-layer-01)',
                borderRadius: 'var(--cds-border-radius)',
                padding: 'var(--cds-spacing-05)',
                marginTop: 'var(--cds-spacing-05)',
              }}
            >
              <DataTable
                rows={Object.entries(data.breakdownByCategory).map(([key, value], index) => ({
                  id: `row-${index}`,
                  category: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                  currentValue: value,
                  projectedSavings: Math.round(value * (data.threeYearROI / 100)),
                  improvementPercent: `${Math.round(((value * (data.productivityGains / 100)) / value) * 100)}%`,
                  paybackMonths: Math.round(data.paybackPeriod * 12 * (value / data.annualSavings)),
                }))}
                headers={[
                  { key: 'category', header: 'Category' },
                  { key: 'currentValue', header: 'Current Value ($)' },
                  { key: 'projectedSavings', header: 'Projected Savings ($)' },
                  { key: 'improvementPercent', header: 'Improvement' },
                  { key: 'paybackMonths', header: 'Payback (Months)' },
                ]}
                render={({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                  <TableContainer>
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {headers.map(header => (
                            <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map(row => (
                          <TableRow {...getRowProps({ row })}>
                            {row.cells.map(cell => (
                              <TableCell key={cell.id}>
                                {cell.info.header === 'currentValue' || cell.info.header === 'projectedSavings'
                                  ? `$${cell.value.toLocaleString()}`
                                  : cell.value}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              />

              <div
                style={{
                  marginTop: 'var(--cds-spacing-05)',
                  padding: 'var(--cds-spacing-05)',
                  backgroundColor: 'var(--cds-layer-02)',
                  borderRadius: 'var(--cds-border-radius)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <strong>Total Annual Savings:</strong> ${data.annualSavings.toLocaleString()}
                </div>
                <div>
                  <strong>Average Payback Period:</strong> {data.paybackPeriod.toFixed(1)} years
                </div>
                <div>
                  <strong>3-Year Total ROI:</strong> {data.threeYearROI}%
                </div>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Tile>
  );
};

export default ROIVisualization;
